import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { FaPaperPlane, FaTimes, FaReply, FaUsers } from "react-icons/fa";
import { useRoomMessages } from "../../../../context/RoomMessagesContext";
import "./MessageInput.css";

/**
 * Get initials from name for avatar fallback
 */
const getInitials = (name) => {
  if (!name) return "??";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Get consistent avatar color based on name
 */
const getAvatarColor = (name) => {
  const colors = [
    "#3b82f6", "#8b5cf6", "#10b981", "#f59e0b",
    "#ec4899", "#06b6d4", "#6366f1", "#14b8a6",
  ];
  const charCode = (name || "U").charCodeAt(0);
  return colors[charCode % colors.length];
};

const MessageInput = ({ roomId, replyTo, onClearReply, roomMembers = [], isLeader = false }) => {
  const { sendMessage, startTyping, stopTyping } = useRoomMessages();

  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionStartPos, setMentionStartPos] = useState(-1);

  const textareaRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const mentionListRef = useRef(null);

  // Filter room members based on query
  const filteredMembers = useMemo(() => {
    const query = mentionQuery.toLowerCase();
    
    // Only show @all and @everyone for leaders (like Slack/Teams)
    const specialMentions = isLeader ? [
      { _id: "all", name: "all", isSpecial: true, description: "Mention everyone in the lab" },
      { _id: "everyone", name: "everyone", isSpecial: true, description: "Mention everyone in the lab" },
    ].filter(m => m.name.includes(query)) : [];
    
    // Filter actual room members
    const memberList = (roomMembers || []).filter(member => {
      const name = member.user?.name || member.name || "";
      const username = member.user?.username || member.username || "";
      return name.toLowerCase().includes(query) || username.toLowerCase().includes(query);
    }).slice(0, 8); // Limit to 8 members for performance
    
    return [...specialMentions, ...memberList];
  }, [mentionQuery, roomMembers, isLeader]);

  // Reset selected index when filtered list changes
  useEffect(() => {
    setSelectedMentionIndex(0);
  }, [filteredMembers.length]);

  // Scroll selected mention into view
  useEffect(() => {
    if (mentionListRef.current && showMentionSuggestions) {
      const selectedElement = mentionListRef.current.querySelector('.msg-mention-item.selected');
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedMentionIndex, showMentionSuggestions]);

  // Prevent send button from stealing focus (keeps keyboard open on mobile)
  const preventFocusLoss = useCallback((e) => {
    e.preventDefault();
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        60
      )}px`;
    }
  }, [content]);

  // Focus textarea when replying
  useEffect(() => {
    if (replyTo && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [replyTo]);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    // Emit typing event
    startTyping(roomId);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping(roomId);
    }, 3000);
  }, [roomId, startTyping, stopTyping]);

  // Handle content change
  const handleContentChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setContent(value);

    // Trigger typing indicator
    if (value.trim()) {
      handleTyping();
    } else {
      stopTyping(roomId);
    }

    // Check for @mentions - find the @ symbol before cursor
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      
      // Check if there's a space or newline after @, which means mention is complete
      if (!textAfterAt.includes(" ") && !textAfterAt.includes("\n")) {
        setMentionQuery(textAfterAt);
        setMentionStartPos(lastAtIndex);
        setShowMentionSuggestions(true);
        return;
      }
    }
    
    setShowMentionSuggestions(false);
    setMentionQuery("");
    setMentionStartPos(-1);
  };

  // Handle send message
  const handleSend = async (e) => {
    e.preventDefault();

    if (!content.trim() || sending) return;

    // Stop typing indicator
    stopTyping(roomId);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    setSending(true);
    try {
      await sendMessage(roomId, content, replyTo?._id);

      // Clear form
      setContent("");
      if (onClearReply) {
        onClearReply();
      }

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    } catch (error) { /* ignored */ } finally {
      setSending(false);
    }
  };

  // Insert mention at cursor position
  const insertMention = useCallback((member) => {
    const mentionName = member.user?.name || member.name || member.username || "";
    const mentionText = member.isSpecial ? mentionName : mentionName.split(" ")[0]; // Use first name for regular members
    
    if (mentionStartPos !== -1) {
      const before = content.substring(0, mentionStartPos);
      const after = content.substring(mentionStartPos + mentionQuery.length + 1);
      const newContent = `${before}@${mentionText} ${after}`;
      
      setContent(newContent);
      setShowMentionSuggestions(false);
      setMentionQuery("");
      setMentionStartPos(-1);
      setSelectedMentionIndex(0);

      // Focus back on textarea and set cursor position
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPos = before.length + mentionText.length + 2; // +2 for @ and space
        setTimeout(() => {
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      }
    }
  }, [content, mentionStartPos, mentionQuery]);

  // Handle Enter key (send on Enter, new line on Shift+Enter)
  const handleKeyDown = (e) => {
    // Handle mention navigation
    if (showMentionSuggestions && filteredMembers.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev < filteredMembers.length - 1 ? prev + 1 : 0
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev > 0 ? prev - 1 : filteredMembers.length - 1
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredMembers[selectedMentionIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowMentionSuggestions(false);
        return;
      }
    }

    // Normal Enter to send
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  // Close mention dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mentionListRef.current && !mentionListRef.current.contains(e.target)) {
        setShowMentionSuggestions(false);
      }
    };
    
    if (showMentionSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMentionSuggestions]);

  return (
    <div className="msg-input-wrap">
      {/* Reply Indicator */}
      {replyTo && (
        <div className="msg-reply-badge">
          <FaReply />
          <span className="msg-reply-text">
            Replying to <strong>{replyTo.sender?.name}</strong>
          </span>
          <button
            onClick={onClearReply}
            className="msg-reply-close"
            type="button"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* @Mention Autocomplete Dropdown */}
      {showMentionSuggestions && filteredMembers.length > 0 && (
        <div className="msg-mentions" ref={mentionListRef}>
          <div className="msg-mentions-header">
            <FaUsers size={12} />
            <span>Mention someone</span>
          </div>
          <div className="msg-mentions-list">
            {filteredMembers.map((member, index) => {
              const displayName = member.user?.name || member.name || "Unknown";
              const username = member.user?.username || member.username || "";
              const photo = member.user?.profilePhotoUrl || member.profilePhotoUrl;
              const isSpecial = member.isSpecial;
              
              return (
                <div
                  key={member._id || index}
                  className={`msg-mention-item ${index === selectedMentionIndex ? 'selected' : ''} ${isSpecial ? 'special' : ''}`}
                  onClick={() => insertMention(member)}
                  onMouseEnter={() => setSelectedMentionIndex(index)}
                >
                  {isSpecial ? (
                    <div className="msg-mention-avatar special-avatar">
                      <FaUsers />
                    </div>
                  ) : photo ? (
                    <img src={photo} alt={displayName} className="msg-mention-avatar" />
                  ) : (
                    <div 
                      className="msg-mention-avatar initials"
                      style={{ backgroundColor: getAvatarColor(displayName) }}
                    >
                      {getInitials(displayName)}
                    </div>
                  )}
                  <div className="msg-mention-info">
                    <span className="msg-mention-name">
                      @{isSpecial ? member.name : displayName.split(" ")[0]}
                    </span>
                    {isSpecial ? (
                      <span className="msg-mention-desc">{member.description}</span>
                    ) : username && (
                      <span className="msg-mention-username">@{username}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="msg-mentions-hint">
            <kbd>↑</kbd><kbd>↓</kbd> to navigate • <kbd>Enter</kbd> to select • <kbd>Esc</kbd> to close
          </div>
        </div>
      )}

      {/* Input Form */}
      <form onSubmit={handleSend} className="msg-input-form">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          placeholder={
            replyTo
              ? `Reply to ${replyTo.sender?.name}...`
              : "Type a message... Use @ to mention"
          }
          disabled={sending}
          className="msg-input-text"
          rows={1}
        />

        <button
          type="submit"
          disabled={sending || !content.trim()}
          className="msg-send-btn"
          title="Send message (Enter)"
          onMouseDown={preventFocusLoss}
          onTouchStart={preventFocusLoss}
        >
          {sending ? <span className="msg-sending">⏳</span> : <FaPaperPlane />}
        </button>
      </form>
    </div>
  );
};

export default MessageInput;
